import React, { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useLanguageCode } from '../../../store/app/appStore';
import styles from './AutoTextfit.module.css';

interface AutoTextfitProps {
  children: React.ReactNode;
  maxFontSize?: number;
  minFontSize?: number;
  className?: string;
}

const AutoTextfit: React.FC<AutoTextfitProps> = ({ 
  children, 
  maxFontSize = 16, 
  minFontSize = 10,
  className 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);
  const languageCode = useLanguageCode();

  useLayoutEffect(() => {
    const fitText = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const parent = container.parentElement;
      if (!parent) return;

      // Reset font size to max and force reflow to get accurate measurements
      let currentSize = maxFontSize;
      container.style.fontSize = `${currentSize}px`;
      const parentWidth = parent.clientWidth - 8; // Subtracting 8px for save area

      while (
        currentSize > minFontSize &&
        (container.scrollWidth > parentWidth || container.scrollHeight > parent.clientHeight)
      ) {
        currentSize -= 1;
        container.style.fontSize = `${currentSize}px`;
      }

      setFontSize(currentSize);
    };

    // Fit synchronously before first paint so the text doesn't flash at max size and then shrink
    fitText();

    // Fallback re-fit for async translation loading
    const timeoutId = setTimeout(fitText, 100);

    window.addEventListener('resize', fitText);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', fitText);
    };
  }, [children, maxFontSize, minFontSize, languageCode]);

  // The size goes in as a custom property read by the static class: as a styled-component prop, every new size
  // generated a new class, and inserting its rule into the styled-components sheet made Chromium re-match that whole
  // sheet against the document (crash-game cards mounting during scroll each fit to their own size).
  const sizeStyle: CSSProperties & { '--auto-textfit-size': string } = { '--auto-textfit-size': `${fontSize}px` };

  return (
    <div ref={containerRef} className={className ? `${styles.text} ${className}` : styles.text} style={sizeStyle}>
      {children}
    </div>
  );
};

export default AutoTextfit;